import React, { useState } from 'react';
import { UserPlus, Users, ChevronDown, CalendarCheck } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import { useComptesGarEnAttente } from '../../hooks/fetch_api_hook/useComptesGarEnAttente';

/**
 * Bandeau « comptes GAR en attente » + « données à jour du … » (spec §10).
 * L'API dit QUOI faire pour chaque compte (a_candidats) :
 *   - a_candidats: false → aucune fiche ne ressemble → un ENTRANT → l'action
 *     est au DÉLÉGUÉ : réimporter l'établissement (le rattachement est auto) ;
 *   - a_candidats: true  → une fiche de sa classe lui ressemble → l'action est
 *     au PROF DE LA CLASSE : arbitrer au guichet Synchronisation GAR (un
 *     réimport ne tranchera pas l'ambiguïté à sa place).
 * Affiché en tête de la page Import établissement (étab GAR uniquement).
 */

function SousListe({ ouvert, onToggle, teinte, icone: Icone, titre, action, comptes }) {
    const teintes = {
        ambre: {
            boite: 'bg-amber-50 border-amber-300', hover: 'hover:bg-amber-100/60',
            pastille: 'bg-amber-500', titre: 'text-amber-900', texte: 'text-amber-800',
            chevron: 'text-amber-600', chip: 'border-amber-200',
        },
        bleu: {
            boite: 'bg-blue-50 border-blue-300', hover: 'hover:bg-blue-100/60',
            pastille: 'bg-blue-500', titre: 'text-blue-900', texte: 'text-blue-800',
            chevron: 'text-blue-600', chip: 'border-blue-200',
        },
    }[teinte];

    return (
        <div className={`border rounded-xl overflow-hidden ${teintes.boite}`}>
            <button
                onClick={onToggle}
                className={`w-full flex items-center gap-2.5 px-4 py-3 text-left transition-colors ${teintes.hover}`}
            >
                <div className={`p-1.5 rounded-lg flex-shrink-0 ${teintes.pastille}`}>
                    <Icone className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${teintes.titre}`}>{titre}</p>
                    <p className={`text-xs ${teintes.texte}`}>{action}</p>
                </div>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${teintes.chevron} ${ouvert ? 'rotate-180' : ''}`} />
            </button>
            {ouvert ? (
                <div className="px-4 pb-3 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {comptes.map((c) => (
                        <span key={c.id_gar} className={`px-2 py-0.5 rounded-lg bg-white border text-xs text-gray-700 ${teintes.chip}`}>
                            {c.nom} {c.prenom}{c.classe_nom ? ` · ${c.classe_nom}` : ''}
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function ComptesEnAttenteGar({ refresh }) {
    const { etablissementData } = useUser();
    const [ouvertImport, setOuvertImport] = useState(false);
    const [ouvertArbitrage, setOuvertArbitrage] = useState(false);
    const { data } = useComptesGarEnAttente(
        etablissementData?.id, !!etablissementData?.is_gar, refresh);

    if (!etablissementData?.is_gar) return null;

    const dernierImport = data?.dernier_import_le
        ? new Date(data.dernier_import_le).toLocaleDateString('fr-FR', {
            day: 'numeric', month: 'long', year: 'numeric',
        })
        : null;
    const comptes = data?.comptes || [];
    const aImporter = comptes.filter((c) => !c.a_candidats);
    const aArbitrer = comptes.filter((c) => c.a_candidats);

    return (
        <div className="max-w-4xl mx-auto mb-3 space-y-2">
            {/* Fraîcheur des données */}
            <div className="flex items-center gap-2 text-xs text-slate-500 px-1">
                <CalendarCheck className="w-3.5 h-3.5" />
                {dernierImport
                    ? `Données de l'établissement à jour du ${dernierImport}`
                    : "Aucun import effectué pour l'instant"}
            </div>

            {/* ⏳ Entrants sans aucune fiche candidate → l'action est au délégué */}
            {aImporter.length > 0 ? (
                <SousListe
                    ouvert={ouvertImport}
                    onToggle={() => setOuvertImport(!ouvertImport)}
                    teinte="ambre"
                    icone={UserPlus}
                    titre={`⏳ ${aImporter.length} élève${aImporter.length > 1 ? 's' : ''} en attente d'import`}
                    action="Aucune fiche ne leur correspond : refaites un import de l'établissement ci-dessous : ils seront rattachés automatiquement."
                    comptes={aImporter}
                />
            ) : null}

            {/* 👤 Une fiche leur ressemble → l'action est au prof de la classe */}
            {aArbitrer.length > 0 ? (
                <SousListe
                    ouvert={ouvertArbitrage}
                    onToggle={() => setOuvertArbitrage(!ouvertArbitrage)}
                    teinte="bleu"
                    icone={Users}
                    titre={`👤 ${aArbitrer.length} élève${aArbitrer.length > 1 ? 's' : ''} en attente d'arbitrage`}
                    action="Une fiche de leur classe leur ressemble : le prof de la classe doit les lier via la Synchronisation GAR. Un réimport ne tranchera pas à sa place."
                    comptes={aArbitrer}
                />
            ) : null}
        </div>
    );
}

export default ComptesEnAttenteGar;
